import type { JSX, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The files a page offers (AP-100). Pages register them; the one download
 * button in the header lists them above the offers every page has (PDF,
 * Excel, Mappe). No page carries its own "Export" or "CSV" button.
 */
export interface Download {
  key: string;
  label: string;
  hint?: string;
  /** A file from the server; the browser sends the session cookie. */
  href?: string;
  /** A view inside PST, e.g. a print view. */
  to?: string;
  onClick?: () => void;
}

interface Registry {
  items: Download[];
  set: (owner: string, items: Download[] | null) => void;
}

const Ctx = createContext<Registry>({ items: [], set: () => undefined });

export function DownloadsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [byOwner, setByOwner] = useState<Record<string, Download[]>>({});
  // Stable, so a page's effect does not run again because another page registered.
  const set = useCallback((owner: string, items: Download[] | null) => setByOwner((all) => {
    const next = { ...all };
    if (items) next[owner] = items; else delete next[owner];
    return next;
  }), []);
  const value = useMemo<Registry>(() => ({ items: Object.values(byOwner).flat(), set }), [byOwner, set]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The downloads registered by the pages now shown. */
export function useDownloads(): Download[] {
  return useContext(Ctx).items;
}

/**
 * Registers a page's downloads while it is shown. `owner` names the page;
 * the list is compared by its content, so a new array each render is fine.
 */
export function usePageDownloads(owner: string, items: Download[]): void {
  const { set } = useContext(Ctx);
  const signature = JSON.stringify(items.map(({ onClick: _, ...rest }) => rest));
  useEffect(() => {
    set(owner, items);
    return () => set(owner, null);
  }, [owner, signature, set]); // items by content (signature), see above
}
